import type { StoryFunnelReport, StoryReportParams } from "./story-funnel/types";

type ViteEnv = { env: Record<string, string | undefined> };

const ADMIN_API_BASE: string =
  (import.meta as unknown as ViteEnv).env.VITE_ADMIN_API_BASE ?? "";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${ADMIN_API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = (await res.json()) as { detail?: string };
      detail = body.detail ?? "";
    } catch {
      detail = await res.text();
    }
    throw new ApiError(res.status, detail || res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type AuthConfigResponse = {
  enabled: boolean;
  google_client_id: string;
  allowed_email_domain: string;
};

export type AuthMeResponse = {
  user: {
    email: string;
    name: string;
    picture?: string | null;
  } | null;
  auth_required: boolean;
};

export type GoogleLoginResponse = {
  user: {
    email: string;
    name: string;
    picture?: string | null;
  };
};

export const api = {
  authConfig: () => request<AuthConfigResponse>("/auth/config"),
  me: () => request<AuthMeResponse>("/auth/me"),
  googleLogin: (code: string) =>
    request<GoogleLoginResponse>("/auth/google", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  logout: () => request<{ message: string }>("/auth/logout", { method: "POST" }),
  storyFunnel: (params: StoryReportParams, signal?: AbortSignal) => {
    const query = new URLSearchParams(params);
    return request<StoryFunnelReport>(
      `/api/admin/analytics/story?${query.toString()}`,
      signal ? { signal } : undefined,
    );
  },
  funnel: (params: FunnelParams) => {
    const query = new URLSearchParams({
      slug: params.slug,
      from: params.from,
      to: params.to,
      question_ids: params.questionIds.join(","),
      question_types: Object.entries(params.questionTypes)
        .map(([id, t]) => `${id}:${t}`)
        .join(","),
      // Top-line only. Per-question breakdowns are fetched lazily via
      // api.breakdown so no single request computes every question's breakdown
      // over the full window (that inline pass exceeded the 30s API timeout for
      // high-traffic quizzes). Steps still carry breakdown_kind; responses=[].
      include_breakdowns: "false",
    });
    if (params.cohort && params.cohort !== "all") query.set("cohort", params.cohort);
    if (params.persona) query.set("persona", params.persona);
    // Omitted when unsplit, so a request from this build against an API that
    // predates the parameter is byte-identical to the one it has always sent.
    if (params.variant && params.variant !== "all") {
      query.set("variant", params.variant);
    }
    return request<FunnelResponse>(`/api/admin/analytics/funnel?${query.toString()}`);
  },
  breakdown: (params: BreakdownParams) => {
    const query = new URLSearchParams({
      slug: params.slug,
      from: params.from,
      to: params.to,
      question_id: params.questionId,
      question_type: params.questionType,
    });
    if (params.cohort && params.cohort !== "all") query.set("cohort", params.cohort);
    if (params.persona) query.set("persona", params.persona);
    return request<QuestionBreakdown>(
      `/api/admin/analytics/breakdown?${query.toString()}`,
    );
  },
  submissions: (params: {
    slug?: string;
    persona?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const query = new URLSearchParams();
    if (params.slug) query.set("slug", params.slug);
    if (params.persona) query.set("persona", params.persona);
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("page_size", String(params.pageSize));
    const qs = query.toString();
    return request<SubmissionsResponse>(
      `/api/admin/submissions${qs ? `?${qs}` : ""}`,
    );
  },
  exportSubmissions: (params: ResponseExportParams, signal?: AbortSignal) => {
    const query = new URLSearchParams({ slug: params.slug });
    if (params.from !== undefined) query.set("from", params.from);
    if (params.to !== undefined) query.set("to", params.to);
    if (params.pageSize !== undefined) query.set("page_size", String(params.pageSize));
    if (params.afterId !== undefined) query.set("after_id", String(params.afterId));
    if (params.snapshotId !== undefined) query.set("snapshot_id", String(params.snapshotId));
    return request<ResponseExportPage>(
      `/api/admin/submissions/export?${query.toString()}`,
      signal ? { signal } : undefined,
    );
  },
  experimentStatus: (slug: string) =>
    request<ExperimentStatusResponse>(
      `/api/admin/experiments/status?slug=${encodeURIComponent(slug)}`,
    ),
};

export type ExperimentStatusResponse = {
  sessions_with_variant_last_hour: number;
};

export type BreakdownKind = "options" | "free_text" | "skip";

export type ResponseBreakdown = {
  answer_id: string;
  count: number;
  pct_of_answered: number;
  pct_of_started: number;
  orders: number;
  // null means SUPPRESSED, not zero. `orders` always carries the raw count, so
  // the UI never has to infer which. The server owns the threshold; see
  // `cvr_min_orders`.
  cvr: number | null;
};

export type CvrBaseline = {
  sessions: number;
  orders: number;
  cvr: number | null;
};

export type FunnelStep = {
  key: string;
  label: string;
  question_id: string | null;
  viewed: number;
  answered: number;
  drop_off_pct: number | null;
  exits: number;
  time_on_page_ms: number | null;
  breakdown_kind: BreakdownKind;
  denominator_answered: number;
  denominator_started: number;
  responses: ResponseBreakdown[];
};

export type DailyPoint = {
  date: string;
  visitors: number;
  submits: number;
  avg_duration_ms: number | null;
};

export type DeviceBreakdown = {
  desktop: number;
  mobile: number;
  tablet: number;
  unknown: number;
};

// The experiment-arm filter, in the wire spelling the API accepts. The rollup
// sentinels (`*all*` / `*none*`) are deliberately not part of it -- the server
// maps these four words, so nothing off the wire reaches a rollup row's arm
// column verbatim (see api/variants.py).
export type ArmFilter = "all" | "control" | "variant" | "none";

// What the API says it APPLIED, which is not necessarily what was asked for.
// `variant` is optional on purpose: the admin SPA and the API deploy in parallel
// and the SPA lands first (rule 23f), so an API that predates the parameter
// answers an arm-filtered request with unsplit numbers and no `variant` key at
// all. Typing it as required would make that state unrepresentable in types
// while remaining entirely possible on the wire -- which is how a deploy-skew
// bug becomes a blank dashboard instead of a warning.
export type AppliedFacets = {
  cohort: string;
  persona: string | null;
  variant?: string;
};

// The rollup spelling the API echoes back for "Intelligems resolved no arm".
// Compared against, never sent.
export const APPLIED_ARM_NONE = "*none*";
export const APPLIED_ARM_ALL = "*all*";

export type Facets = {
  applied: AppliedFacets;
  persona_counts: Record<string, number>;
};

export type FunnelResponse = {
  quiz_slug: string;
  started: number;
  completed: number;
  completion_rate: number | null;
  avg_duration_ms: number | null;
  steps: FunnelStep[];
  daily: DailyPoint[];
  devices: DeviceBreakdown;
  facets: Facets;
  // Nullable because the server time-boxes the baseline statement and sends
  // null when it expires -- NOT MEASURED, which is a different claim from a
  // measured zero. Both read sites already cope: the hero hides on null, and
  // PagesTable's zeroed fallback leaves `cvr: null`, on which
  // ResponseBreakdownRow already suppresses every delta.
  cvr_baseline: CvrBaseline | null;
  cvr_min_orders: number;
  // Endgame counts. Additive on the API, and the admin deploys ahead of it
  // (rule 23f), so every read site uses `?? 0` -- typing them required is
  // correct for the new API and says nothing about what an older deployment
  // puts on the wire.
  offer_viewed: number;
  email_gate_viewed: number;
  email_gate_submitted: number;
  // Emails the quiz itself asked for and got. NOT every email on a quiz row:
  // the identity back-fill joins far more, and those measure the store rather
  // than the quiz.
  emails_captured: number;
};

export type FunnelParams = {
  slug: string;
  from: string;
  to: string;
  questionIds: string[];
  questionTypes: Record<string, string>;
  cohort?: "all" | "completers" | "abandoners";
  persona?: string;
  // Quiz-internal funnel only. There is deliberately no equivalent on
  // BreakdownParams: per-answer distributions are not split by arm (rule 25a
  // keeps conversion whole, and the live per-question scan is unindexed on this
  // column), and the route would silently ignore the param rather than error.
  variant?: ArmFilter;
};

export type BreakdownParams = {
  slug: string;
  from: string;
  to: string;
  questionId: string;
  questionType: string;
  cohort?: "all" | "completers" | "abandoners";
  persona?: string;
};

// No baseline / threshold here: both come from the FUNNEL response, which is
// the single source. A per-question copy would be a second number that could
// diverge from the one the row deltas are actually measured against.
export type QuestionBreakdown = {
  question_id: string;
  breakdown_kind: BreakdownKind;
  denominator_answered: number;
  denominator_started: number;
  responses: ResponseBreakdown[];
};

export type SubmissionRow = {
  id: number;
  quiz_slug: string;
  persona: string;
  email: string | null;
  phone: string | null;
  klaviyo_synced_at: string | null;
  klaviyo_error: string | null;
  created_at: string;
  intelligems_variant: string | null;
  session_id: string | null;
};

export type SubmissionsResponse = {
  total: number;
  items: SubmissionRow[];
  page: number;
  page_size: number;
};

export type ResponseExportParams = {
  slug: string;
  from?: string;
  to?: string;
  pageSize?: number;
  afterId?: number;
  snapshotId?: number;
};

export type ResponseExportRow = {
  id: number;
  quiz_slug: string;
  email: string | null;
  purchased: boolean;
  answers: Record<string, unknown>;
  created_at: string;
};

export type ResponseExportPage = {
  items: ResponseExportRow[];
  total: number;
  snapshot_id: number;
  next_after_id: number | null;
};
